<template>
  <FriedDropdown
    :main-axis-offset="8"
    :placement="placement"
    :close-on-outside-click="!dropdownOpen"
    class="timeline-filter-dropdown"
  >
    <template #trigger="{ open }">
      <FilterButton
        data-external="timeline-filter-button"
        :text="filterButtonText"
        :active="open"
        :active-filter="hasFiltersApplied"
        @toggle-active="$emit('toggle-filter')"
      />
    </template>
    <template #content>
      <div v-click-outside="handleClickOutside" class="filter-content" @click.stop>
        <FriedH4 class="title">{{ t('dr_timeline_filter_by') }}</FriedH4>
        <div class="fields">
          <FriedSelect
            append-to-body
            :label="t('name')"
            :options="availableParticipants"
            option-label="fullName"
            option-key="id"
            class="participant-select"
            searchable
            :model-value="selectedParticipantId"
            @update:model-value="selectParticipant"
            @mousedown="dropdownOpen = true"
          />
          <FriedSelect
            append-to-body
            :label="t('dr_timeline_category')"
            class="category-select"
            :model-value="selectedCategory"
            :options="categoryOptions"
            option-key="category"
            option-label="label"
            @update:model-value="selectCategory"
            @mousedown="dropdownOpen = true"
          />
          <FriedDateTimeInput
            data-external="timeline-filter-from-date"
            :model-value="fromDateTime"
            append-to-body
            :disabled-dates="disabledFromDates"
            class="time-interval-from"
            :label="t('dr_timeline_from_date')"
            @update:model-value="setFromDateTime"
            @mousedown="dropdownOpen = true"
          />
          <FriedDateTimeInput
            data-external="timeline-filter-to-date"
            :model-value="toDateTime"
            append-to-body
            :disabled-dates="disabledToDates"
            class="time-interval-to"
            :label="t('dr_timeline_to_date')"
            @update:model-value="setToDateTime"
            @mousedown="dropdownOpen = true"
          />
        </div>
        <FriedButton
          v-if="hasFiltersApplied"
          :button-type="ButtonType.Secondary"
          data-external="timeline-filter-clear"
          class="timeline-filter-clear-button"
          @click.stop="clearFilters"
        >
          <template #leftIcon>
            <FriedIcon icon="close-circle" />
          </template>
          {{ t('dr_timeline_clear_filters') }}
        </FriedButton>
      </div>
    </template>
  </FriedDropdown>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, ref, watchEffect } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ButtonType, AvatarSize, Placement } from '@getaccept/fried-tofu';
import { DateTime } from 'luxon';
import FilterButton from '@getaccept/lib-shared/src/components/FilterButton.vue';
import AvatarHelper from '../../helpers/avatar.helper';
import TimelineHelper from '../helpers/timeline.helper';
import TimelineConstants from '../constants/timeline.constants';
import type { TimelineEventCategory } from '../types/timeline.types';
import type { Participant } from '../../types/participant';

export default defineComponent({
  components: { FilterButton },
  props: {
    allParticipants: {
      type: Array as PropType<Participant[]>,
      required: true,
    },
    dsrCreatedAt: {
      type: Number,
      required: true,
    },
    placement: {
      type: String as PropType<Placement>,
      default: Placement.BottomEnd,
    },
  },
  emits: ['update-filter', 'reset-filter', 'update-filter-applied', 'toggle-filter'],
  setup(props, { emit }) {
    const dropdownOpen = ref(false);
    const availableParticipants = computed(() =>
      [...props.allParticipants].map(p => ({
        id: p.id,
        fullName: p?.fullName || p?.email || '',
      }))
    );

    const selectedParticipantId = ref<string | null>(null);
    const selectedCategory = ref<TimelineEventCategory | null>(null);

    const defaultFromDateTime = DateTime.fromMillis(props.dsrCreatedAt).startOf('day').toJSDate();
    const defaultToDateTime = DateTime.now().endOf('day').toJSDate();

    const fromDateTime = ref<string | null>(defaultFromDateTime.toISOString());
    const toDateTime = ref<string | null>(defaultToDateTime.toISOString());

    const disabledFromDates = computed(() => ({
      to: DateTime.fromMillis(props.dsrCreatedAt).minus({ days: 1 }).toJSDate(),
      from: toDateTime.value
        ? DateTime.fromISO(toDateTime.value).toJSDate()
        : DateTime.now().toJSDate(),
    }));

    const disabledToDates = computed(() => ({
      to: fromDateTime.value
        ? DateTime.fromISO(fromDateTime.value).toJSDate()
        : DateTime.fromMillis(props.dsrCreatedAt).toJSDate(),
      from: DateTime.now().toJSDate(),
    }));

    const timeInterval = computed(() => {
      if (!fromDateTime.value || !toDateTime.value) {
        return null;
      }
      return TimelineHelper.calculateTimeInterval(
        new Date(fromDateTime.value),
        new Date(toDateTime.value)
      );
    });

    const setFromDateTime = (value: Date) => {
      fromDateTime.value = value.toISOString();
      if (!toDateTime.value) {
        toDateTime.value = defaultToDateTime.toISOString();
      }

      if (fromDateTime.value > toDateTime.value) {
        toDateTime.value = value.toISOString();
      }
      dropdownOpen.value = false;

      emit('update-filter', {
        timeInterval: timeInterval.value,
      });
    };

    const setToDateTime = (value: Date) => {
      toDateTime.value = value.toISOString();
      if (!fromDateTime.value) {
        fromDateTime.value = defaultFromDateTime.toISOString();
      }
      if (toDateTime.value < fromDateTime.value) {
        fromDateTime.value = value.toISOString();
      }
      dropdownOpen.value = false;

      emit('update-filter', {
        timeInterval: timeInterval.value,
      });
    };

    const hasFiltersApplied = computed(
      () =>
        [selectedParticipantId.value, selectedCategory.value].some(value => value !== null) ||
        fromDateTime.value !== defaultFromDateTime.toISOString() ||
        toDateTime.value !== defaultToDateTime.toISOString()
    );

    watchEffect(() => {
      emit('update-filter-applied', hasFiltersApplied.value);
    });

    const filterButtonText = computed(() =>
      hasFiltersApplied.value ? t('dr_timeline_filter_active') : t('dr_timeline_add_filter')
    );

    const clearFilters = () => {
      selectedParticipantId.value = null;
      selectedCategory.value = null;
      fromDateTime.value = defaultFromDateTime.toISOString();
      toDateTime.value = defaultToDateTime.toISOString();

      emit('reset-filter');
    };

    const selectParticipant = (participantId: string) => {
      selectedParticipantId.value = participantId;
      dropdownOpen.value = false;
      emit('update-filter', {
        participantIds: [participantId],
      });
    };

    const selectCategory = (category: TimelineEventCategory) => {
      selectedCategory.value = category;
      dropdownOpen.value = false;
      const eventTypes = TimelineConstants.eventTypesByCategory(category);
      if (!eventTypes.length) return;

      emit('update-filter', {
        eventTypes,
      });
    };

    const handleClickOutside = (event: MouseEvent) => {
      const isClickInsideDropdown = (event.target as HTMLElement).classList.contains(
        'select-option'
      );
      dropdownOpen.value = isClickInsideDropdown;
    };

    return {
      t,
      ButtonType,
      AvatarSize,
      selectedParticipantId,
      selectedCategory,
      availableParticipants,
      getDisplayName: AvatarHelper.getDisplayName,
      categoryOptions: TimelineConstants.eventCategoriesOptions(),
      fromDateTime,
      toDateTime,
      disabledFromDates,
      disabledToDates,
      setFromDateTime,
      setToDateTime,
      hasFiltersApplied,
      clearFilters,
      selectParticipant,
      selectCategory,
      dropdownOpen,
      filterButtonText,
      Placement,
      handleClickOutside,
    };
  },
});
</script>

<style lang="scss" scoped>
.timeline-filter-dropdown {
  z-index: 2;

  .filter-content {
    padding: var(--spacing-50);

    .title {
      margin-bottom: var(--spacing-150);
    }

    .fields {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(3, 1fr);
      gap: var(--spacing-100);

      .participant-select {
        grid-area: 1 / 1 / 2 / 3;
      }

      .category-select {
        grid-area: 2 / 1 / 3 / 3;
      }

      .time-interval-from {
        grid-area: 3 / 1 / 4 / 2;
      }

      .time-interval-to {
        grid-area: 3 / 2 / 4 / 3;
      }
    }

    .timeline-filter-clear-button {
      width: 100%;
      margin-top: var(--spacing-150);
      display: flex;
      gap: var(--spacing-50);
    }

    @media screen and (min-width: $sm) {
      min-width: 32rem;
    }
  }
}
</style>
